import { CURRENCY_OPTIONS } from "@/constants";
import { getExchangeRate } from "@/apis/sap.api";
import Big from "big.js";
import { Button, Form, InputNumber, Popover, Select, Space } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface PricePopupProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: {
    price: number;
    priceUnit: string;
    priceHkd: number;
  }) => void;
  initialValues?: { price?: number; priceUnit?: string; priceHkd?: number };
  children: React.ReactNode;
}

export const PricePopup = (props: PricePopupProps) => {
  const { t } = useTranslation("purchaseRequestManagement");
  const { t: tCommon } = useTranslation("common");

  const { open, onClose, onSave, initialValues, children } = props;
  const [form] = Form.useForm();

  // 计算 HKD 金额
  const price = Form.useWatch("price", form);
  const priceUnit = Form.useWatch("priceUnit", form);

  useEffect(() => {
    if (open && initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [open, initialValues, form]);

  useEffect(() => {
    if (price && priceUnit && priceUnit !== "HKD") {
      const calculateHkd = async () => {
        try {
          const res = await getExchangeRate(priceUnit, "HKD");
          const rate: number = res.data.data;
          const priceHkd = new Big(price)
            .times(new Big(rate))
            .round(2)
            .toNumber();
          form.setFieldsValue({ priceHkd });
        } catch {
          form.setFieldsValue({ priceHkd: undefined });
        }
      };
      calculateHkd();
    } else if (priceUnit === "HKD") {
      form.setFieldsValue({ priceHkd: price });
    }
  }, [price, priceUnit, form]);

  const handleOk = async () => {
    await form.validateFields();
    const values = form.getFieldsValue();
    onSave(values);
    onClose();
  };

  const content = (
    <Form disabled={false} form={form} layout="vertical" style={{ width: 320 }}>
      <Form.Item
        name="price"
        label={t("单价")}
        rules={[
          {
            required: true,
            message: t("请输入单价"),
          },
        ]}
      >
        <InputNumber
          min={0}
          precision={2}
          style={{ width: "100%" }}
          placeholder={t("请输入")}
        />
      </Form.Item>
      <Form.Item
        name="priceUnit"
        label={t("单价单位")}
        rules={[
          {
            required: true,
            message: t("请选择币种"),
          },
        ]}
      >
        <Select options={CURRENCY_OPTIONS} placeholder={t("请选择")} />
      </Form.Item>
      <Form.Item name="priceHkd" label={t("单价HKD")}>
        <InputNumber
          min={0}
          precision={2}
          style={{ width: "100%" }}
          placeholder={t("自动计算")}
          disabled
        />
      </Form.Item>
      <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button onClick={onClose}>{tCommon("取消")}</Button>
          <Button type="primary" onClick={handleOk}>
            {tCommon("确认")}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(visible) => !visible && onClose()}
      content={content}
      trigger="click"
      placement="bottom"
    >
      {children}
    </Popover>
  );
};
